import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  MapPin,
  Phone,
  Clock,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Navigation,
  Compass,
  Home,
  Car,
  Building2,
  BookOpen,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for a coaching centre near me in Ghazipur. Please share course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "96/B", label: "Badi Bagh, Ghazipur", icon: MapPin },
  { value: "10+", label: "Areas Served", icon: Navigation },
  { value: "95%", label: "Success Rate", icon: TrendingUp },
  { value: "340+", label: "Students Enrolled", icon: Users },
];

const AREAS_SERVED = [
  "Badi Bagh", "Ghazipur Sadar", "Zamania", "Saidpur",
  "Mohammadabad", "Karanda", "Dildarnagar", "Yusufpur",
  "Mahuabagh", "Old GT Road area",
];

const WHY_NEARBY = [
  { icon: Clock, title: "Less Travel, More Study Time", desc: "A coaching centre close to home means students save commute time and use it for revision and rest instead." },
  { icon: Home, title: "Convenient for Parents", desc: "Easy drop-offs, pickups and parent-teacher meetings — without long travel across the city." },
  { icon: Building2, title: "Walk-in Enquiries Welcome", desc: "Visit our Badi Bagh campus anytime during working hours to see the classrooms and meet faculty before enrolling." },
  { icon: Compass, title: "Central, Well-Connected Location", desc: "Located at Badi Bagh, Ghazipur — easily reachable from Sadar, Zamania, Saidpur, Mohammadabad and surrounding areas." },
  { icon: Car, title: "Easy to Find", desc: "Located opposite Raj Nursing Home, Badi Bagh — a well-known landmark in Ghazipur city." },
  { icon: Users, title: "Local Faculty, Local Understanding", desc: "Our teachers know the local school syllabi and exam patterns followed across Ghazipur, giving students an extra edge." },
];

const ALL_COURSES = [
  { label: "Class 8 Foundation", to: "/courses/class-8-foundation" },
  { label: "Class 9 Foundation", to: "/courses/class-9-foundation" },
  { label: "Class 10 Board Preparation", to: "/courses/class-10-board-preparation" },
  { label: "Class 11 Foundation (PCM/PCB)", to: "/courses/class-11-foundation" },
  { label: "Class 12 Board Preparation (PCM/PCB)", to: "/courses/class-12-board-preparation" },
  { label: "Computer Science", to: "/courses/computer-science" },
];

const TESTIMONIALS = [
  { name: "Manoj Kumar (Parent)", area: "Ghazipur Sadar", rating: 5, text: "We searched for a good coaching centre near our home and found SSS Achievers at Badi Bagh. It's close, easy to reach, and the teaching quality is excellent." },
  { name: "Simran Khan", area: "Near Zamania", rating: 5, text: "I was looking for a coaching centre near me that doesn't compromise on quality. SSS Achievers in Badi Bagh was the perfect choice — close by and genuinely the best in Ghazipur." },
  { name: "Devendra Yadav (Parent)", area: "Mohammadabad", rating: 5, text: "Dropping my son off is easy since SSS Achievers is centrally located in Ghazipur. The small batches and results speak for themselves." },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function CoachingCentreNearMeGhazipur() {
  return (
    <>
      <Helmet>
        <title>Coaching Centre Near Me in Ghazipur | SSS Achievers — Badi Bagh</title>
        <meta
          name="description"
          content="Looking for a coaching centre near you in Ghazipur? SSS Achievers, Badi Bagh is centrally located and easy to reach from Sadar, Zamania, Saidpur, Mohammadabad & Karanda. Expert coaching for Class 8-12. Free demo class."
        />
        <meta
          name="keywords"
          content="best coaching classes near me, near me coaching centre, near by coaching centre, coaching centre near me Ghazipur, coaching classes near me Ghazipur, tuition centre near me, coaching institute near me Ghazipur, SSS Achievers Badi Bagh"
        />
        <link rel="canonical" href="https://www.sssachievers.com/coaching-centre-near-me-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Coaching Centre Near Me in Ghazipur | SSS Achievers" />
        <meta property="og:description" content="SSS Achievers, Badi Bagh — a coaching centre near you in Ghazipur. Easy to reach from Sadar, Zamania, Saidpur, Mohammadabad & Karanda. Free demo class." />
        <meta property="og:url" content="https://www.sssachievers.com/coaching-centre-near-me-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Coaching Centre Near Me in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="SSS Achievers, Badi Bagh — your nearby coaching centre in Ghazipur. Free demo class available." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Coaching Centre Near Me Ghazipur", "Nearby Coaching Centre Ghazipur"],
                "description": "SSS Achievers is a centrally located coaching centre in Badi Bagh, Ghazipur, easily reachable from Sadar, Zamania, Saidpur, Mohammadabad and Karanda.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh, Opp. Raj Nursing Home",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "areaServed": [
                  "Badi Bagh", "Ghazipur Sadar", "Zamania", "Saidpur", "Mohammadabad", "Karanda", "Dildarnagar", "Yusufpur"
                ],
                "openingHoursSpecification": [
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
                    "opens": "09:00",
                    "closes": "19:00"
                  }
                ],
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Where is the nearest good coaching centre in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers, located at 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001, is one of the most centrally located and trusted coaching centres in Ghazipur, easily reachable from most parts of the city."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Which areas near Ghazipur does SSS Achievers serve?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers welcomes students from across Ghazipur city and surrounding areas including Badi Bagh, Ghazipur Sadar, Zamania, Saidpur, Mohammadabad, Karanda, Dildarnagar and Yusufpur."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can I walk in for a free demo class without an appointment?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — you can visit the SSS Achievers campus at Badi Bagh during working hours (Mon-Sat, 9 AM - 7 PM) for a free demo class. Calling ahead on +91 99185 19991 helps us prepare for your visit."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What are the landmarks near SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is located opposite Raj Nursing Home in Badi Bagh, Ghazipur — a well-known landmark, making it easy to find from anywhere in the city."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does a nearby coaching centre mean better quality?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Proximity saves travel time, but quality matters most. SSS Achievers combines a convenient, central Ghazipur location with a 95% success rate, expert faculty and small batch sizes — so you get both convenience and quality."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Coaching Centre Near Me - Ghazipur", "item": "https://www.sssachievers.com/coaching-centre-near-me-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Coaching Centre Near Me</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">Centrally Located — Badi Bagh, Ghazipur</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Looking for a <span className="text-[#0097b2]">Coaching Centre Near You</span> in Ghazipur?
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers is conveniently located at Badi Bagh, opposite Raj Nursing Home — easy to reach from Ghazipur Sadar, Zamania, Saidpur, Mohammadabad, Karanda and nearby areas. Quality coaching, right near you.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo Class <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["Central Location", "Easy to Find", "Walk-ins Welcome", "Free Demo Class"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-2xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Rated by 340+ local students</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Open</p>
                    <p className="text-2xl font-bold">9–7</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ WHY NEARBY MATTERS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why a <span className="text-[#0097b2]">Nearby Coaching Centre</span> Makes a Difference
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {WHY_NEARBY.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ AREAS SERVED ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Areas We <span className="text-[#0097b2]">Serve Near Ghazipur</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">Students join us from across the city and these nearby areas</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {AREAS_SERVED.map((area, i) => (
                <motion.span key={area} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 border-gray-100 shadow-sm text-sm font-medium text-slate-700">
                  <MapPin className="w-3.5 h-3.5 text-[#0097b2]" />
                  {area}
                </motion.span>
              ))}
            </div>
          </div>
        </section>

        {/* ══ MAP & DIRECTIONS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Find Us — <span className="text-[#0097b2]">SSS Achievers, Badi Bagh</span>
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Our campus is located opposite Raj Nursing Home in Badi Bagh — a central, well-known landmark that's easy to reach by walking, cycle, two-wheeler or shared auto from anywhere in Ghazipur city.
                </p>
                {[
                  { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                  { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                  { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                ].map(({ icon: Icon, text, href }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                  </div>
                ))}
                <a
                  href="https://maps.google.com/?q=96/B+Badi+Bagh+Ghazipur+UP+233001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-white bg-[#0097b2] px-4 py-2 rounded-lg hover:bg-[#007a91] transition"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Get Directions on Google Maps
                </a>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">SSS Achievers — Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Coaching Centre Near Me Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ ALL COURSES ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                All Courses Available at <span className="text-[#0097b2]">This Coaching Centre</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">Class 8 to Class 12, plus Computer Science — all under one roof</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {ALL_COURSES.map(({ label, to }, i) => (
                <motion.div key={to} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}>
                  <Link to={to} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="group flex items-center justify-between gap-2 p-4 rounded-xl border-2 border-gray-100 hover:border-[#0097b2]/40 bg-white hover:shadow-md transition-all">
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <BookOpen className="w-4 h-4 text-[#0097b2]" />
                      {label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#0097b2] group-hover:translate-x-1 transition-all" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What <span className="text-[#0097b2]">Local Families</span> Say
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {TESTIMONIALS.map(({ name, area, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{area}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About finding a coaching centre near you in Ghazipur</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Where is the nearest good coaching centre in Ghazipur?", a: "SSS Achievers, located at 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001, is one of the most centrally located and trusted coaching centres in Ghazipur, easily reachable from most parts of the city." },
                { q: "Which areas near Ghazipur does SSS Achievers serve?", a: "SSS Achievers welcomes students from across Ghazipur city and surrounding areas including Badi Bagh, Ghazipur Sadar, Zamania, Saidpur, Mohammadabad, Karanda, Dildarnagar and Yusufpur." },
                { q: "Can I walk in for a free demo class without an appointment?", a: "Yes — you can visit the SSS Achievers campus at Badi Bagh during working hours (Mon-Sat, 9 AM - 7 PM) for a free demo class. Calling ahead on +91 99185 19991 helps us prepare for your visit." },
                { q: "What are the landmarks near SSS Achievers?", a: "SSS Achievers is located opposite Raj Nursing Home in Badi Bagh, Ghazipur — a well-known landmark, making it easy to find from anywhere in the city." },
                { q: "Does a nearby coaching centre mean better quality?", a: "Proximity saves travel time, but quality matters most. SSS Achievers combines a convenient, central Ghazipur location with a 95% success rate, expert faculty and small batch sizes — so you get both convenience and quality." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <MapPin className="w-4 h-4 text-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Your Nearby Coaching Centre in Ghazipur</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Visit SSS Achievers — Right Near You in Ghazipur</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Book a free demo class today or simply walk in to our Badi Bagh campus to experience SSS Achievers for yourself.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Class <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
